import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { Mail, Users, Building2, LogOut, RefreshCw, Download, Lock, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "promill_admin_key";

const AdminPage = () => {
    const [adminKey, setAdminKey] = useState("");
    const [authed, setAuthed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("newsletter");
    const [data, setData] = useState({ newsletter: [], contacts: [], franchise: [] });

    // Try stored key on mount
    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) tryLogin(stored, true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const tryLogin = async (key, silent = false) => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/api/admin/verify`, { headers: { "X-Admin-Key": key } });
            if (!res.ok) throw new Error("Invalid key");
            localStorage.setItem(STORAGE_KEY, key);
            setAdminKey(key);
            setAuthed(true);
            if (!silent) toast.success("Welcome, admin.");
        } catch (_e) {
            localStorage.removeItem(STORAGE_KEY);
            setAuthed(false);
            if (!silent) toast.error("Invalid admin key.");
        } finally {
            setLoading(false);
        }
    };

    const fetchAll = useCallback(async () => {
        if (!adminKey) return;
        setLoading(true);
        try {
            const opts = { headers: { "X-Admin-Key": adminKey } };
            const [n, c, f] = await Promise.all([
                fetch(`${API}/api/admin/newsletter`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/contacts`, opts).then((r) => r.json()),
                fetch(`${API}/api/admin/franchise`, opts).then((r) => r.json()),
            ]);
            setData({ newsletter: n.items || [], contacts: c.items || [], franchise: f.items || [] });
        } catch (_e) {
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    }, [adminKey]);

    useEffect(() => { if (authed) fetchAll(); }, [authed, fetchAll]);

    const logout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setAdminKey("");
        setAuthed(false);
        setData({ newsletter: [], contacts: [], franchise: [] });
    };

    const deleteRow = async (id) => {
        if (!window.confirm("Delete this record? This cannot be undone.")) return;
        try {
            const res = await fetch(`${API}/api/admin/${tab}/${id}`, {
                method: "DELETE",
                headers: { "X-Admin-Key": adminKey },
            });
            if (!res.ok) throw new Error("Failed");
            toast.success("Record deleted");
            setData((cur) => ({ ...cur, [tab]: cur[tab].filter((r) => r.id !== id) }));
        } catch (_e) {
            toast.error("Failed to delete");
        }
    };

    const downloadCSV = () => {
        const rows = data[tab];
        if (!rows.length) return toast.info("Nothing to export");
        const keys = Object.keys(rows[0]);
        const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `promill-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!authed) {
        return (
            <div className="min-h-screen bg-pm-black text-pm-cream flex items-center justify-center px-4">
                <Toaster position="top-center" richColors />
                <form
                    onSubmit={(e) => { e.preventDefault(); tryLogin(adminKey); }}
                    data-testid="admin-login-form"
                    className="w-full max-w-md bg-white/5 border border-white/10 rounded-2xl p-8"
                >
                    <div className="h-12 w-12 rounded-xl bg-pm-yellow text-pm-green flex items-center justify-center mb-5">
                        <Lock className="w-6 h-6" />
                    </div>
                    <h1 className="text-2xl font-extrabold">ProMill Admin</h1>
                    <p className="mt-1 text-sm text-pm-cream/60">Enter your admin key to access the panel.</p>
                    <input
                        type="password"
                        value={adminKey}
                        onChange={(e) => setAdminKey(e.target.value)}
                        data-testid="admin-key-input"
                        placeholder="Admin key"
                        className="mt-5 w-full h-12 px-4 rounded-xl bg-white text-pm-black border border-white/10 focus:outline-none focus:ring-2 focus:ring-pm-yellow"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-button"
                        className="mt-4 w-full h-12 rounded-xl bg-pm-yellow text-pm-black font-bold hover:bg-pm-yellowBright transition-colors disabled:opacity-50"
                    >
                        {loading ? "Verifying..." : "Enter"}
                    </button>
                </form>
            </div>
        );
    }

    const TABS = [
        { id: "newsletter", label: "Newsletter", icon: Mail, count: data.newsletter.length },
        { id: "contacts", label: "Contact Enquiries", icon: Users, count: data.contacts.length },
        { id: "franchise", label: "Franchise Enquiries", icon: Building2, count: data.franchise.length },
    ];

    const rows = data[tab];

    return (
        <div data-testid="admin-page" className="min-h-screen bg-pm-cream">
            <Toaster position="top-center" richColors />
            <header className="bg-pm-black text-pm-cream px-4 sm:px-6 lg:px-8 py-4 sticky top-0 z-40 border-b border-pm-cream/10">
                <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-pm-yellow font-bold">ProMill</div>
                        <h1 className="text-xl font-extrabold">Admin Panel</h1>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={fetchAll} data-testid="admin-refresh" disabled={loading} className="h-10 px-4 rounded-lg border border-pm-cream/20 hover:bg-pm-cream/5 flex items-center gap-2 text-sm disabled:opacity-50">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                        <button onClick={logout} data-testid="admin-logout" className="h-10 px-4 rounded-lg border border-pm-cream/20 hover:bg-red-500/10 hover:border-red-400 flex items-center gap-2 text-sm">
                            <LogOut className="w-4 h-4" />
                            Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {TABS.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full transition-colors ${tab === t.id ? "bg-pm-green text-pm-yellow" : "bg-white border border-pm-black/10 text-pm-black hover:border-pm-green"}`}
                        >
                            <t.icon className="w-4 h-4" />
                            <span className="font-bold text-sm">{t.label}</span>
                            <span className={`ml-1 text-xs font-bold px-2 py-0.5 rounded-full ${tab === t.id ? "bg-pm-yellow text-pm-green" : "bg-pm-yellow/30 text-pm-green"}`}>{t.count}</span>
                        </button>
                    ))}
                    <button onClick={downloadCSV} data-testid="admin-export-csv" className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-pm-yellow text-pm-black font-bold text-sm hover:bg-pm-yellowBright">
                        <Download className="w-4 h-4" /> Export CSV
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl border border-pm-black/8 overflow-hidden">
                    {rows.length === 0 ? (
                        <div data-testid="admin-empty" className="py-16 text-center text-pm-black/50">No entries yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-pm-sand text-pm-black/70 text-xs uppercase tracking-wider">
                                    <tr>
                                        {Object.keys(rows[0]).filter((k) => k !== "id").map((k) => (
                                            <th key={k} className="text-left px-4 py-3 font-bold">{k.replace(/_/g, " ")}</th>
                                        ))}
                                        <th className="text-right px-4 py-3 font-bold w-16">Actions</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="admin-table-body">
                                    {rows.map((r, i) => (
                                        <tr key={r.id || i} className="border-t border-pm-black/5 hover:bg-pm-cream/50">
                                            {Object.keys(r).filter((k) => k !== "id").map((k) => (
                                                <td key={k} className="px-4 py-3 text-pm-black/85 align-top">
                                                    {k === "created_at" ? new Date(r[k]).toLocaleString() : (r[k] || "—")}
                                                </td>
                                            ))}
                                            <td className="px-4 py-3 text-right align-top">
                                                <button
                                                    onClick={() => deleteRow(r.id)}
                                                    data-testid={`admin-delete-${r.id}`}
                                                    aria-label="Delete record"
                                                    title="Delete"
                                                    className="h-9 w-9 rounded-lg text-pm-black/50 hover:text-white hover:bg-red-500 inline-flex items-center justify-center transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminPage;
